// AHB-expression parser: turns an `ahb_expression` string into an AST.
//
// Grammar mirrors maco-validator core/conditions/expression_eval.py
// (recursive descent, precedence OR < XOR < AND). Tokens:
//   [N]       -> condition ref      { type: "ref", key: "N" }
//   [NP0..1]  -> package ref         { type: "package", key: "9P", cardinality: "0..1" }
//   ∧ (U+2227) AND, ∨ (U+2228) OR, ⊻ (U+22BB) XOR
//   juxtaposition / whitespace between two [..] = implicit AND
//   ( ... )   grouping
//
// A leading STATUS word (Muss|Soll|Kann|X|S) is split off into `status`.

import type { AhbExprNode, AhbExpression } from "@/types/regelwerk";

const AND_OP = "∧"; // ∧
const OR_OP = "∨"; // ∨
const XOR_OP = "⊻"; // ⊻

const STATUS_WORDS = ["Muss", "Soll", "Kann", "X", "S"] as const;

type Token =
  | { kind: "ref"; value: string }
  | { kind: "op"; value: string }
  | { kind: "lparen" }
  | { kind: "rparen" };

export function parseAhbExpression(raw: string): AhbExpression {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { status: null, tree: null, raw };
  }

  const { status, rest } = splitStatus(trimmed);

  if (rest.trim() === "") {
    return { status, tree: null, raw };
  }

  const tokens = tokenize(rest);
  if (tokens.length === 0) {
    return { status, tree: null, raw };
  }

  const parser = new Parser(tokens);
  const tree = parser.parseOr();
  return { status, tree, raw };
}

/** Split a leading status word (only when it is its own token at the start). */
function splitStatus(expr: string): { status: string | null; rest: string } {
  for (const word of STATUS_WORDS) {
    if (expr === word) {
      return { status: word, rest: "" };
    }
    if (expr.startsWith(word)) {
      const next = expr[word.length];
      // Status word must be delimited by whitespace (not glued to a bracket
      // or another letter, e.g. "Soll" must not match inside "Sollwert").
      if (next === " " || next === "\t" || next === "\n") {
        return { status: word, rest: expr.slice(word.length) };
      }
    }
  }
  return { status: null, rest: expr };
}

function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expr.length) {
    const c = expr[i];
    if (c === " " || c === "\t" || c === "\n") {
      i += 1;
      continue;
    }
    if (c === "[") {
      const j = expr.indexOf("]", i);
      if (j !== -1) {
        tokens.push({ kind: "ref", value: expr.slice(i + 1, j) });
        i = j + 1;
        continue;
      }
    }
    if (c === "(") {
      tokens.push({ kind: "lparen" });
      i += 1;
      continue;
    }
    if (c === ")") {
      tokens.push({ kind: "rparen" });
      i += 1;
      continue;
    }
    if (c === AND_OP || c === OR_OP || c === XOR_OP) {
      tokens.push({ kind: "op", value: c });
      i += 1;
      continue;
    }
    // Unknown character: skip it (matches the validator's lenient tokenizer).
    i += 1;
  }
  return tokens;
}

/** Build a leaf node from a bracket key like "931" or "9P0..1". */
function makeLeaf(key: string): AhbExprNode {
  const trimmed = key.trim();
  const pkgMatch = /^(\d+P)(.*)$/.exec(trimmed);
  if (pkgMatch) {
    const cardinality = pkgMatch[2].trim();
    return cardinality
      ? { type: "package", key: pkgMatch[1], cardinality }
      : { type: "package", key: pkgMatch[1] };
  }
  return { type: "ref", key: trimmed };
}

/**
 * Combine a left node with same-operator children into a single flattened
 * node. Splices in the left node's children when it is already the same
 * operator so `[1] ∧ [2] ∧ [3]` becomes one `and` with three children.
 */
function combine(
  type: "and" | "or" | "xor",
  left: AhbExprNode,
  right: AhbExprNode,
): AhbExprNode {
  const children: AhbExprNode[] =
    left.type === type ? [...left.children] : [left];
  children.push(right);
  return { type, children };
}

class Parser {
  private pos = 0;
  constructor(private readonly tokens: Token[]) {}

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  parseOr(): AhbExprNode {
    let left = this.parseXor();
    let t = this.peek();
    while (t && t.kind === "op" && t.value === OR_OP) {
      this.pos += 1;
      const right = this.parseXor();
      left = combine("or", left, right);
      t = this.peek();
    }
    return left;
  }

  private parseXor(): AhbExprNode {
    let left = this.parseAnd();
    let t = this.peek();
    while (t && t.kind === "op" && t.value === XOR_OP) {
      this.pos += 1;
      const right = this.parseAnd();
      left = combine("xor", left, right);
      t = this.peek();
    }
    return left;
  }

  private parseAnd(): AhbExprNode {
    let left = this.parsePrimary();
    let t = this.peek();
    while (t) {
      if (t.kind === "op" && t.value === AND_OP) {
        this.pos += 1;
        const right = this.parsePrimary();
        left = combine("and", left, right);
      } else if (t.kind === "ref" || t.kind === "lparen") {
        // Implicit AND: two operands without an operator between them.
        const right = this.parsePrimary();
        left = combine("and", left, right);
      } else {
        break;
      }
      t = this.peek();
    }
    return left;
  }

  private parsePrimary(): AhbExprNode {
    const t = this.peek();
    if (!t) {
      // Defensive: should not happen for well-formed input.
      return { type: "ref", key: "" };
    }
    if (t.kind === "lparen") {
      this.pos += 1;
      const inner = this.parseOr();
      const close = this.peek();
      if (close && close.kind === "rparen") {
        this.pos += 1;
      }
      return inner;
    }
    if (t.kind === "ref") {
      this.pos += 1;
      return makeLeaf(t.value);
    }
    // Stray operator / rparen: consume and produce an empty ref so we make
    // progress rather than looping.
    this.pos += 1;
    return { type: "ref", key: "" };
  }
}
